import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { isApiError } from '../api/client';
import { describeError } from '../components/toast';
import { useApi } from '../api/context';
import { useToast } from '../components/toast';
import { strings } from '../strings';

const s = strings.variables;

/**
 * The secret actions of the Variables window in deployment scope (spec 6.10.11–12): Accept, Ignore, Rewrite
 * tags, Prepare vault, and their bulk forms (one request per secret, one after the other, with a progress
 * toast and a summary). Every write invalidates the deployment's queries.
 */
export function useSecretActions(deploymentId: string | null) {
  const api = useApi();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  /** "Accepting 2 of 3…" while a bulk action runs (shown in the bulk bar, a polite live region). */
  const [progress, setProgress] = useState<string | null>(null);
  /** Secrets being recovered and apps whose references are being refreshed (their buttons show progress). */
  const [recovering, setRecovering] = useState<ReadonlySet<string>>(new Set());
  const [refreshing, setRefreshing] = useState<ReadonlySet<string>>(new Set());
  /** Feature spec 15: apps whose rollout request is in flight, and the rollouts this user started. */
  const [restarting, setRestarting] = useState<ReadonlySet<string>>(new Set());
  const [started, setStarted] = useState<ReadonlySet<string>>(new Set());

  const invalidate = useCallback(() => {
    if (deploymentId) void queryClient.invalidateQueries({ queryKey: ['deployment', deploymentId] });
  }, [deploymentId, queryClient]);

  const run = useCallback(
    async (action: () => Promise<unknown>, success: string) => {
      setBusy(true);
      try {
        await action();
        toast.show({ tone: 'success', title: success });
      } catch (error) {
        toast.error(error);
      } finally {
        setBusy(false);
        invalidate();
      }
    },
    [invalidate, toast],
  );

  const bulk = useCallback(
    async (names: readonly string[], kind: 'accept' | 'ignore') => {
      if (!deploymentId || names.length === 0) return;
      const verb = kind === 'accept' ? s.bulk.accepting : s.bulk.ignoring;
      const done = kind === 'accept' ? s.bulk.accepted : s.bulk.ignored;
      setBusy(true);
      const failed: string[] = [];
      let ok = 0;
      for (const [i, name] of names.entries()) {
        setProgress(s.bulk.progress(verb, i + 1, names.length));
        try {
          if (kind === 'accept') await api.acceptSecret(deploymentId, name);
          else await api.ignoreSecret(deploymentId, name);
          ok++;
        } catch (error) {
          failed.push(`${name}: ${describeError(error).detail ?? describeError(error).title}`);
        }
      }
      setBusy(false);
      setProgress(null);
      invalidate();
      toast.show({ tone: failed.length ? 'warning' : 'success', title: s.bulk.summary(ok, done, failed) });
    },
    [api, deploymentId, invalidate, toast],
  );

  /** Recover (spec 6.10.11): the server waits up to a minute; 504 `RECOVERY_PENDING` is a warning. */
  const recover = useCallback(
    async (name: string) => {
      if (!deploymentId) return;
      const key = name.toLowerCase();
      setRecovering((current) => new Set(current).add(key));
      try {
        await api.recoverSecret(deploymentId, name);
        toast.show({ tone: 'success', title: s.maintenance.recovered(name) });
      } catch (error) {
        if (isApiError(error, 'RECOVERY_PENDING'))
          toast.show({ tone: 'warning', title: s.maintenance.recoveryPending(name) });
        else toast.error(error);
      } finally {
        setRecovering((current) => {
          const next = new Set(current);
          next.delete(key);
          return next;
        });
        invalidate();
      }
    },
    [api, deploymentId, invalidate, toast],
  );

  /** Refresh references of an app (spec 6.10.11); a 409 names the restart as the alternative. */
  const refreshReferences = useCallback(
    async (resourceId: string, nowActionLabel: string | null) => {
      if (!deploymentId) return;
      setRefreshing((current) => new Set(current).add(resourceId));
      try {
        await api.refreshReferences(deploymentId, resourceId);
        toast.show({ tone: 'success', title: s.maintenance.refreshed(resourceId) });
      } catch (error) {
        if (isApiError(error, 'REFRESH_NOT_SUPPORTED') && nowActionLabel) {
          const { title, detail } = describeError(error);
          toast.show({
            tone: 'error',
            title,
            detail: [detail, s.maintenance.useNowAction(nowActionLabel)].filter(Boolean).join(' '),
          });
        } else toast.error(error);
      } finally {
        setRefreshing((current) => {
          const next = new Set(current);
          next.delete(resourceId);
          return next;
        });
        invalidate();
      }
    },
    [api, deploymentId, invalidate, toast],
  );

  /**
   * Rolls an app out (feature spec 15 5.9): `POST …/resources/{id}/restart`. The toast comes from the platform's
   * `nowActionDoneText`, or the server's error; a provider failure is a 200 with `succeeded: false`.
   */
  const restartApp = useCallback(
    async (resourceId: string, texts: { done: string; failed: (error: string) => string }) => {
      if (!deploymentId) return;
      setRestarting((current) => new Set(current).add(resourceId));
      try {
        const result = await api.restartApp(deploymentId, resourceId);
        if (result.succeeded) {
          setStarted((current) => new Set(current).add(resourceId));
          toast.show({ tone: 'success', title: texts.done });
        } else toast.show({ tone: 'error', title: texts.failed(result.error ?? '') });
      } catch (error) {
        toast.error(error);
      } finally {
        setRestarting((current) => {
          const next = new Set(current);
          next.delete(resourceId);
          return next;
        });
        invalidate();
      }
    },
    [api, deploymentId, invalidate, toast],
  );

  return {
    busy,
    progress,
    invalidate,
    restartApp,
    isRestarting: (resourceId: string) => restarting.has(resourceId),
    /** Rollouts this user started (their end is announced). */
    startedRollouts: started,
    forgetRollout: (resourceId: string) =>
      setStarted((current) => {
        const next = new Set(current);
        next.delete(resourceId);
        return next;
      }),
    recover,
    isRecovering: (name: string) => recovering.has(name.toLowerCase()),
    refreshReferences,
    isRefreshing: (resourceId: string) => refreshing.has(resourceId),
    accept: (name: string) =>
      deploymentId && run(() => api.acceptSecret(deploymentId, name), strings.secrets.accepted),
    ignore: (name: string) =>
      deploymentId && run(() => api.ignoreSecret(deploymentId, name), strings.secrets.ignored(name)),
    rewriteTags: () =>
      deploymentId && run(() => api.rewriteSecretTags(deploymentId), strings.secrets.tagsRewritten),
    bulk,
    prepareVault: async () => {
      if (!deploymentId) return;
      setBusy(true);
      try {
        const created = await api.createRun(deploymentId, { kind: 'apply', scope: 'vault' });
        invalidate();
        void queryClient.invalidateQueries({ queryKey: ['runs'] });
        navigate(`/runs/${created.id}`);
      } catch (error) {
        toast.error(error);
      } finally {
        setBusy(false);
      }
    },
  };
}

export type SecretActions = ReturnType<typeof useSecretActions>;
